import { supabase } from '../lib/supabase';

class ArtistContentService {
  // Get artist about section
  async getArtistAbout(userId) {
    try {
      const { data, error } = await supabase?.from('artist_about')?.select('*')?.eq('user_id', userId)?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching artist about:', error);
      throw new Error(`Failed to load artist about: ${error?.message}`);
    }
  }

  // Update artist about section with proper unique constraint handling
  async updateArtistAbout(userId, updates) {
    try {
      // Use the safe upsert function to prevent duplicates
      const { data, error } = await supabase?.rpc('upsert_artist_about', {
        p_user_id: userId,
        p_header_image_path: updates?.header_image_path || null,
        p_about_text: updates?.about_text || null,
        p_booking_contact: updates?.booking_contact || null,
        p_about_image_path: updates?.about_image_path || null
      });

      if (error) throw error;
      
      // Return the UUID from the function directly instead of fetching
      // The function returns the ID of the created/updated record
      if (data) {
        // Fetch the complete updated record using the returned ID
        const { data: updatedRecord, error: fetchError } = await supabase
          ?.from('artist_about')
          ?.select('*')
          ?.eq('id', data) // Use the returned ID instead of user_id
          ?.single();
        
        if (fetchError) {
          console.warn('Could not fetch updated record, but upsert succeeded:', fetchError);
          // Return a basic object with the essential data instead of throwing
          return {
            id: data,
            user_id: userId,
            header_image_path: updates?.header_image_path || null,
            about_text: updates?.about_text || null,
            booking_contact: updates?.booking_contact || null,
            updated_at: new Date()?.toISOString()
          };
        }
        
        return updatedRecord;
      } else {
        throw new Error('Upsert function did not return a valid ID');
      }
    } catch (error) {
      console.error('Error updating artist about:', error);
      throw new Error(`Failed to update artist about: ${error?.message}`);
    }
  }

  // Update artist promotions with proper unique constraint handling
  async updateArtistPromotions(userId, updates) {
    try {
      // Use the safe upsert function to prevent duplicates
      const { data, error } = await supabase?.rpc('upsert_artist_promotions', {
        p_user_id: userId,
        p_splash_image_path: updates?.splash_image_path || null,
        p_home_image_path: updates?.home_image_path || null,
        p_logo_image_path: updates?.logo_image_path || null,
        p_featured_item: updates?.featured_item || null,
        p_homepage_promos: updates?.homepage_promos || null
      });

      if (error) throw error;
      
      // Return the UUID from the function directly instead of fetching
      if (data) {
        // Fetch the complete updated record using the returned ID
        const { data: updatedRecord, error: fetchError } = await supabase
          ?.from('artist_promotions')
          ?.select('*')
          ?.eq('id', data) // Use the returned ID instead of user_id
          ?.single();
        
        if (fetchError) {
          console.warn('Could not fetch updated record, but upsert succeeded:', fetchError);
          // Return a basic object with the essential data instead of throwing
          return {
            id: data,
            user_id: userId,
            splash_image_path: updates?.splash_image_path || null,
            home_image_path: updates?.home_image_path || null,
            logo_image_path: updates?.logo_image_path || null,
            featured_item: updates?.featured_item || null,
            homepage_promos: updates?.homepage_promos || null,
            updated_at: new Date()?.toISOString()
          };
        }
        
        return updatedRecord;
      } else {
        throw new Error('Upsert function did not return a valid ID');
      }
    } catch (error) {
      console.error('Error updating artist promotions:', error);
      throw new Error(`Failed to update artist promotions: ${error?.message}`);
    }
  }

  // Get artist promotions
  async getArtistPromotions(userId) {
    try {
      const { data, error } = await supabase?.from('artist_promotions')?.select('*')?.eq('user_id', userId)?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching artist promotions:', error);
      throw new Error(`Failed to load artist promotions: ${error?.message}`);
    }
  }

  // Get artist events (calendar)
  async getArtistEvents(userId) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.select('*')?.eq('user_id', userId)?.order('event_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching artist events:', error);
      throw new Error(`Failed to load artist events: ${error?.message}`);
    }
  }

  // Create new artist event
  async createArtistEvent(userId, eventData) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.insert({
          user_id: userId,
          ...eventData
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating artist event:', error);
      throw new Error(`Failed to create artist event: ${error?.message}`);
    }
  }

  // Update artist event
  async updateArtistEvent(eventId, updates) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', eventId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating artist event:', error);
      throw new Error(`Failed to update artist event: ${error?.message}`);
    }
  }

  // Delete artist event
  async deleteArtistEvent(eventId) {
    try {
      const { error } = await supabase?.from('artist_events')?.delete()?.eq('id', eventId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting artist event:', error);
      throw new Error(`Failed to delete artist event: ${error?.message}`);
    }
  }

  // Get artist works
  async getArtistWorks(userId) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.select('*')?.eq('user_id', userId)?.order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching artist works:', error);
      throw new Error(`Failed to load artist works: ${error?.message}`);
    }
  }

  // Create new artist work
  async createArtistWork(userId, workData) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.insert({
          user_id: userId,
          ...workData
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating artist work:', error);
      throw new Error(`Failed to create artist work: ${error?.message}`);
    }
  }

  // Update artist work
  async updateArtistWork(workId, updates) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', workId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating artist work:', error);
      throw new Error(`Failed to update artist work: ${error?.message}`);
    }
  }

  // Delete artist work
  async deleteArtistWork(workId) {
    try {
      const { error } = await supabase?.from('artist_works')?.delete()?.eq('id', workId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting artist work:', error);
      throw new Error(`Failed to delete artist work: ${error?.message}`);
    }
  }

  // Get shop items (works that are included in shop)
  async getShopItems(userId) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.select('*')?.eq('user_id', userId)?.eq('include_in_shop', true)?.order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching shop items:', error);
      throw new Error(`Failed to load shop items: ${error?.message}`);
    }
  }

  // Update work sort order (for drag and drop)
  async updateWorkSortOrder(workId, sortOrder) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.update({ 
          sort_order: sortOrder,
          updated_at: new Date()?.toISOString()
        })?.eq('id', workId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating work sort order:', error);
      throw new Error(`Failed to update work order: ${error?.message}`);
    }
  }

  // Update event sort order
  async updateEventSortOrder(eventId, sortOrder) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.update({ 
          sort_order: sortOrder,
          updated_at: new Date()?.toISOString()
        })?.eq('id', eventId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating event sort order:', error);
      throw new Error(`Failed to update event order: ${error?.message}`);
    }
  }

  // Get comprehensive artist content for admin view
  async getAllArtistContent(userId) {
    try {
      const [profile, about, promotions, events, works] = await Promise.all([
        this.getUserProfile(userId),
        this.getArtistAbout(userId),
        this.getArtistPromotions(userId),
        this.getArtistEvents(userId),
        this.getArtistWorks(userId)
      ]);

      const shop = works?.filter(work => work?.include_in_shop) || [];

      return {
        profile,
        about,
        promotions,
        events,
        works,
        shop
      };
    } catch (error) {
      console.error('Error fetching all artist content:', error);
      throw new Error(`Failed to load artist content: ${error?.message}`);
    }
  }

  // Get promotion content - alias for getArtistPromotions
  async getPromotionContent(userId) {
    try {
      const data = await this.getArtistPromotions(userId);
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching promotion content:', error);
      return { data: null, error };
    }
  }

  // Save promotion content - alias for updateArtistPromotions with enhanced error handling
  async savePromotionContent(userId, updates) {
    const promotionData = {
      splash_image_path: updates?.splashImagePath,
      home_image_path: updates?.homeImagePath,
      logo_image_path: updates?.logoImagePath,
      featured_item: updates?.featuredItem,
      homepage_promos: updates?.homepagePromos
    };
    
    try {
      const data = await this.updateArtistPromotions(userId, promotionData);
      return { data, error: null };
    } catch (error) {
      console.error('Error saving promotion content:', error);
      return { data: null, error };
    }
  }

  // Helper method to get user profile
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error(`Failed to load user profile: ${error?.message}`);
    }
  }

  // Bulk operations for admin efficiency
  async bulkUpdateWorkStatus(workIds, status) {
    try {
      const { data, error } = await supabase?.from('artist_works')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.in('id', workIds)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk updating work status:', error);
      throw new Error(`Failed to bulk update work status: ${error?.message}`);
    }
  }

  async bulkUpdateEventStatus(eventIds, status) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.in('id', eventIds)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk updating event status:', error);
      throw new Error(`Failed to bulk update event status: ${error?.message}`);
    }
  }
}

const artistContentService = new ArtistContentService();

// Create service objects with specific methods
export const promotionsService = {
  getPromotionContent: (userId) => artistContentService?.getPromotionContent(userId),
  savePromotionContent: (userId, updates) => artistContentService?.savePromotionContent(userId, updates),
  
  // Homepage promos specific methods
  async getHomepagePromos(userId) {
    try {
      const { data, error } = await supabase?.from('artist_promotions')?.select('homepage_promos')?.eq('user_id', userId)?.single();
      
      if (error && error?.code !== 'PGRST116') {
        return { data: null, error };
      }
      
      // Return default structure if no data exists
      const defaultPromos = {
        calendar: { image_path: null, featured_title: '', featured_subtitle: '', button_text: '', external_url: '', link_to_external: false },
        work: { image_path: null, featured_title: '', featured_subtitle: '', button_text: '', external_url: '', link_to_external: false },
        shop: { image_path: null, featured_title: '', featured_subtitle: '', button_text: '', external_url: '', link_to_external: false },
        social: { image_path: null, featured_title: '', featured_subtitle: '', button_text: '', external_url: '', link_to_external: false }
      };
      
      return { data: data?.homepage_promos || defaultPromos, error: null };
    } catch (error) {
      console.error('Error fetching homepage promos:', error);
      return { data: null, error };
    }
  },

  async saveHomepagePromos(userId, homepagePromosData) {
    try {
      const { data, error } = await supabase?.rpc('upsert_artist_promotions', {
        p_user_id: userId,
        p_homepage_promos: homepagePromosData
      });

      if (error) throw error;
      
      if (data) {
        // Fetch the updated record to return complete data
        const { data: updatedRecord, error: fetchError } = await supabase
          ?.from('artist_promotions')
          ?.select('homepage_promos')
          ?.eq('id', data)
          ?.single();
          
        if (fetchError) {
          console.warn('Could not fetch updated homepage promos, but upsert succeeded:', fetchError);
          return { data: homepagePromosData, error: null };
        }
        
        return { data: updatedRecord?.homepage_promos, error: null };
      }
      
      throw new Error('Upsert function did not return a valid ID');
    } catch (error) {
      console.error('Error saving homepage promos:', error);
      return { data: null, error };
    }
  }
};

export const aboutService = {
  getArtistAbout: (userId) => artistContentService?.getArtistAbout(userId),
  updateArtistAbout: (userId, updates) => artistContentService?.updateArtistAbout(userId, updates),
  getAboutContent: (userId) => artistContentService?.getArtistAbout(userId),
  saveAboutContent: async (userId, updates) => {
    const aboutData = {
      header_image_path: updates?.headerImagePath,
      about_text: updates?.aboutText,
      booking_contact: updates?.bookingContact,
      about_image_path: updates?.aboutImagePath
    };
    
    try {
      const data = await artistContentService?.updateArtistAbout(userId, aboutData);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

export const calendarService = {
  async getEvents(userId) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.select('*')?.eq('user_id', userId)?.order('sort_order', { ascending: true })?.order('event_date', { ascending: true });

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Failed to fetch events' } };
    }
  },

  async createEvent(userId, eventData) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.insert([
          {
            user_id: userId,
            title: eventData?.title,
            body: eventData?.body || '',
            event_date: eventData?.date,
            event_time: eventData?.time || null,
            event_url: eventData?.url || null,
            has_url: eventData?.hasUrl || false,
            image_path: eventData?.imagePath || null,
            event_type: eventData?.eventType || 'Performance',
            price: eventData?.price || null,
            venue_name: eventData?.venueName || null,
            venue_address: eventData?.venueAddress || null,
            sort_order: eventData?.sortOrder || 0,
            status: 'published'
          }
        ])?.select();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Failed to create event' } };
    }
  },

  async updateEvent(eventId, eventData) {
    try {
      const updateData = {
        title: eventData?.title,
        body: eventData?.body || '',
        event_date: eventData?.date,
        event_time: eventData?.time || null,
        event_url: eventData?.url || null,
        has_url: eventData?.hasUrl || false,
        image_path: eventData?.imagePath || null,
        event_type: eventData?.eventType || 'Performance',
        price: eventData?.price || null,
        venue_name: eventData?.venueName || null,
        venue_address: eventData?.venueAddress || null,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('artist_events')?.update(updateData)?.eq('id', eventId)?.select();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Failed to update event' } };
    }
  },

  async deleteEvent(eventId) {
    try {
      const { data, error } = await supabase?.from('artist_events')?.delete()?.eq('id', eventId);

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Failed to delete event' } };
    }
  },

  async reorderEvents(reorderData) {
    try {
      // Use a transaction-like approach by updating each event individually
      const updates = reorderData?.map(async (item) => {
        return supabase?.from('artist_events')?.update({ sort_order: item?.sort_order })?.eq('id', item?.id);
      });

      const results = await Promise.all(updates);
      
      // Check if any update failed
      const hasError = results?.some(result => result?.error);
      if (hasError) {
        return { data: null, error: { message: 'Failed to reorder some events' } };
      }

      return { data: results, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to reorder events' } };
    }
  }
};

export const worksService = {
  getWorks: async (userId) => {
    try {
      const works = await artistContentService?.getArtistWorks(userId);
      return { data: works, error: null };
    } catch (error) {
      console.error('Error fetching works:', error);
      return { data: [], error };
    }
  },
  createWork: async (userId, workData) => {
    try {
      const data = await artistContentService?.createArtistWork(userId, workData);
      return { data, error: null };
    } catch (error) {
      console.error('Error creating work:', error);
      return { data: null, error };
    }
  },
  updateWork: async (workId, updates) => {
    try {
      const data = await artistContentService?.updateArtistWork(workId, updates);
      return { data, error: null };
    } catch (error) {
      console.error('Error updating work:', error);
      return { data: null, error };
    }
  },
  deleteWork: async (workId) => {
    try {
      await artistContentService?.deleteArtistWork(workId);
      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting work:', error);
      return { data: null, error };
    }
  },
  reorderWorks: async (orderUpdates) => {
    try {
      const updates = await Promise.all(
        orderUpdates?.map(async (update) => 
          await artistContentService?.updateWorkSortOrder(update?.id, update?.sort_order)
        )
      );
      return { data: updates, error: null };
    } catch (error) {
      console.error('Error reordering works:', error);
      return { data: null, error };
    }
  },
  getShopItems: (userId) => artistContentService?.getShopItems(userId),
  
  // Add getUserProfile method to worksService
  getUserProfile: async (userId) => {
    try {
      const profile = await artistContentService?.getUserProfile(userId);
      return profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  // Add updateWorkPageConfig method for work page configuration
  updateWorkPageConfig: async (userId, configUpdates) => {
    try {
      const updateData = {};
      
      // Map the config field names to database column names
      if (configUpdates?.page_type !== undefined) {
        updateData.work_page_type = configUpdates?.page_type;
      }
      if (configUpdates?.page_icon !== undefined) {
        updateData.work_page_icon = configUpdates?.page_icon;
      }
      if (configUpdates?.page_title !== undefined) {
        updateData.work_page_title = configUpdates?.page_title;
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          ...updateData,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating work page config:', error);
      return { data: null, error };
    }
  },

  // Get work page configuration for mobile app
  getWorkPageConfig: async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('work_page_type, work_page_icon, work_page_title, artist_type')
        ?.eq('id', userId)
        ?.single();

      if (error) {
        console.error('Error fetching work page config:', error);
        return { data: null, error };
      }

      // Apply default values based on artist type if fields are null
      let workPageType = data?.work_page_type;
      let workPageIcon = data?.work_page_icon;
      let workPageTitle = data?.work_page_title;

      // Set defaults based on artist type if values are null
      if (!workPageType || !workPageIcon || !workPageTitle) {
        const artistType = data?.artist_type;
        
        switch (artistType) {
          case 'Musician':
            workPageType = workPageType || 'Artist Streaming Platforms';
            workPageIcon = workPageIcon || 'Music';
            workPageTitle = workPageTitle || 'Music';
            break;
          case 'Visual Artist':
            workPageType = workPageType || 'Individual Works Below';
            workPageIcon = workPageIcon || 'Art';
            workPageTitle = workPageTitle || 'Art';
            break;
          case 'Director': case'Actor':
            workPageType = workPageType || 'Individual Works Below';
            workPageIcon = workPageIcon || 'Film';
            workPageTitle = workPageTitle || 'Films';
            break;
          case 'Writer':
            workPageType = workPageType || 'Individual Works Below';
            workPageIcon = workPageIcon || 'Book';
            workPageTitle = workPageTitle || 'Writings';
            break;
          default:
            // Default fallback values
            workPageType = workPageType || 'Individual Works Below';
            workPageIcon = workPageIcon || 'Music';
            workPageTitle = workPageTitle || 'Work';
        }
      }

      return {
        data: {
          workPageType,
          workPageIcon,
          workPageTitle
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching work page config:', error);
      return { data: null, error };
    }
  }
};

export const shopService = {
  // Get shop settings including Printify API key and product preferences
  async getShopSettings(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('settings')
        ?.eq('id', userId)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        return { data: null, error };
      }

      // Extract shop settings from the settings JSONB column
      const shopSettings = data?.settings?.shop || {
        printifyApiKey: '',
        productTypes: {
          tshirts: true,
          posters: true, 
          prints: true,
          bags: false,
          blankets: false,
          sweatshirts: true,
          vinylAlbums: false
        }
      };

      return { data: shopSettings, error: null };
    } catch (error) {
      console.error('Error fetching shop settings:', error);
      return { data: null, error };
    }
  },

  // Save shop settings including Printify API key and product preferences
  async saveShopSettings(userId, shopData) {
    try {
      // First get current settings to preserve other data
      const { data: currentProfile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('settings')
        ?.eq('id', userId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        return { data: null, error: fetchError };
      }

      // Merge shop settings with existing settings
      const currentSettings = currentProfile?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        shop: {
          printifyApiKey: shopData?.printifyApiKey || '',
          productTypes: shopData?.productTypes || shopData,
          lastUpdated: new Date()?.toISOString()
        }
      };

      // Update the user profile with new settings
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          settings: updatedSettings,
          updated_at: new Date()?.toISOString()
        })
        ?.select('settings')
        ?.single();

      if (error) {
        throw error;
      }

      return { 
        data: data?.settings?.shop || updatedSettings?.shop, 
        error: null 
      };
    } catch (error) {
      console.error('Error saving shop settings:', error);
      return { data: null, error };
    }
  },

  // Get only the Printify API key (for sensitive operations)
  async getPrintifyApiKey(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('settings')
        ?.eq('id', userId)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        return { data: null, error };
      }

      const printifyApiKey = data?.settings?.shop?.printifyApiKey || '';
      return { data: printifyApiKey, error: null };
    } catch (error) {
      console.error('Error fetching Printify API key:', error);
      return { data: null, error };
    }
  },

  // Update only the Printify API key
  async updatePrintifyApiKey(userId, apiKey) {
    try {
      // Get current settings
      const { data: currentProfile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('settings')
        ?.eq('id', userId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        return { data: null, error: fetchError };
      }

      const currentSettings = currentProfile?.settings || {};
      const currentShopSettings = currentSettings?.shop || {};

      // Update only the API key while preserving other shop settings
      const updatedSettings = {
        ...currentSettings,
        shop: {
          ...currentShopSettings,
          printifyApiKey: apiKey || '',
          lastUpdated: new Date()?.toISOString()
        }
      };

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          settings: updatedSettings,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) {
        throw error;
      }

      return { 
        data: { printifyApiKey: apiKey }, 
        error: null 
      };
    } catch (error) {
      console.error('Error updating Printify API key:', error);
      return { data: null, error };
    }
  },

  // Get product type preferences
  async getProductTypes(userId) {
    try {
      const { data: shopSettings } = await this.getShopSettings(userId);
      
      const defaultProductTypes = {
        tshirts: true,
        posters: true,
        prints: true,
        bags: false,
        blankets: false,
        sweatshirts: true,
        vinylAlbums: false
      };

      return { 
        data: shopSettings?.productTypes || defaultProductTypes, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching product types:', error);
      return { data: null, error };
    }
  },

  // Update product type preferences
  async updateProductTypes(userId, productTypes) {
    try {
      // Get current settings
      const { data: currentProfile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('settings')
        ?.eq('id', userId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        return { data: null, error: fetchError };
      }

      const currentSettings = currentProfile?.settings || {};
      const currentShopSettings = currentSettings?.shop || {};

      // Update product types while preserving other shop settings
      const updatedSettings = {
        ...currentSettings,
        shop: {
          ...currentShopSettings,
          productTypes: productTypes,
          lastUpdated: new Date()?.toISOString()
        }
      };

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          settings: updatedSettings,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) {
        throw error;
      }

      return { 
        data: productTypes, 
        error: null 
      };
    } catch (error) {
      console.error('Error updating product types:', error);
      return { data: null, error };
    }
  },

  // Validate Printify API key (placeholder for future integration)
  async validatePrintifyApiKey(apiKey) {
    try {
      // TODO: Implement actual Printify API validation
      // For now, just check if the key is not empty and has reasonable length
      if (!apiKey || apiKey?.length < 10) {
        return { isValid: false, error: 'API key appears to be invalid or too short' };
      }

      // Placeholder validation - in real implementation, make API call to Printify
      // const response = await fetch('https://api.printify.com/v1/shops.json', {
      //   headers: { 'Authorization': `Bearer ${apiKey}` }
      // });
      // return { isValid: response.ok, error: response.ok ? null : 'Invalid API key' };

      return { isValid: true, error: null };
    } catch (error) {
      console.error('Error validating Printify API key:', error);
      return { isValid: false, error: error?.message };
    }
  }
};

export const detectMissingPromotionFiles = async (userId) => {
  try {
    // Get promotion data from database
    const { data: promotion, error: promotionError } = await supabase?.from('artist_promotions')?.select('*')?.eq('user_id', userId)?.single();

    if (promotionError || !promotion) {
      return { success: true, missingFiles: [], hasIssues: false };
    }

    const missingFiles = [];
    const bucket = 'artist-promotion-assets';

    // Check each image path for existence in storage
    for (const [field, path] of [
      ['splash_image_path', promotion?.splash_image_path],
      ['home_image_path', promotion?.home_image_path],
      ['logo_image_path', promotion?.logo_image_path]
    ]) {
      if (path) {
        try {
          const { data: fileData, error: fileError } = await supabase?.storage?.from(bucket)?.download(path);
            
          if (fileError || !fileData) {
            missingFiles?.push({
              field,
              path,
              issue: 'File not found in storage'
            });
          }
        } catch (error) {
          missingFiles?.push({
            field,
            path,
            issue: 'Storage access error'
          });
        }
      }
    }

    return {
      success: true,
      missingFiles,
      hasIssues: missingFiles?.length > 0,
      promotionData: promotion
    };

  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Failed to detect missing files'
    };
  }
};

export const cleanupMissingPromotionReferences = async (userId, missingFiles) => {
  try {
    if (!missingFiles || missingFiles?.length === 0) {
      return { success: true, message: 'No cleanup needed' };
    }

    // Prepare update object to null out missing file references
    const updateData = {};
    missingFiles?.forEach(({ field }) => {
      updateData[field] = null;
    });

    const { data, error } = await supabase?.from('artist_promotions')?.update(updateData)?.eq('user_id', userId)?.select();

    if (error) {
      throw error;
    }

    return {
      success: true,
      message: `Cleaned up ${missingFiles?.length} missing file references`,
      updatedData: data?.[0]
    };

  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Failed to cleanup missing references'
    };
  }
};

export const detectMissingWorkFiles = async (userId) => {
  try {
    // Get work data from database
    const { data: works, error: worksError } = await supabase?.from('artist_works')?.select('*')?.eq('user_id', userId);

    if (worksError) {
      throw worksError;
    }

    if (!works || works?.length === 0) {
      return { success: true, missingFiles: [], hasIssues: false };
    }

    const missingFiles = [];
    const bucket = 'artist-work-images';

    // Check each work's image path
    for (const work of works) {
      if (work?.image_path) {
        try {
          const { data: fileData, error: fileError } = await supabase?.storage?.from(bucket)?.download(work?.image_path);
            
          if (fileError || !fileData) {
            missingFiles?.push({
              workId: work?.id,
              field: 'image_path',
              path: work?.image_path,
              title: work?.title,
              issue: 'File not found in storage'
            });
          }
        } catch (error) {
          missingFiles?.push({
            workId: work?.id,
            field: 'image_path',
            path: work?.image_path,
            title: work?.title,
            issue: 'Storage access error'
          });
        }
      }
    }

    return {
      success: true,
      missingFiles,
      hasIssues: missingFiles?.length > 0,
      worksData: works
    };

  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Failed to detect missing work files'
    };
  }
};

export default artistContentService;